import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Avatar,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Person,
  Phone,
  Badge,
  CalendarToday,
  Logout,
  Edit
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { appConfig } from '../../config/app';

const Profile = () => {
  const { state, actions } = useApp();
  const navigate = useNavigate();

  const handleLogout = () => {
    actions.logout();
    navigate('/');
    actions.showSnackbar('Logged out successfully', 'success');
  };

  const userDetails = [
    {
      icon: <Person />,
      label: 'Full Name',
      value: state.user?.name || 'N/A'
    },
    {
      icon: <Phone />,
      label: 'Mobile Number',
      value: state.user?.mobile || 'N/A'
    },
    {
      icon: <Badge />,
      label: 'Role',
      value: state.user?.role || 'user'
    },
    {
      icon: <CalendarToday />,
      label: 'Joined',
      value: state.user?.createdAt 
        ? new Date(state.user.createdAt).toLocaleDateString()
        : 'N/A'
    }
  ];

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'user':
      default:
        return 'primary';
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
        Profile
      </Typography>

      <Grid container spacing={3}>
        {/* Profile Card */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent sx={{ p: 4 }}>
              {/* Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'primary.main',
                    fontSize: '2rem',
                    mr: 3
                  }}
                >
                  {state.user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h5" component="h2" gutterBottom>
                    {state.user?.name || 'User'}
                  </Typography>
                  <Chip
                    label={state.user?.role?.toUpperCase() || 'USER'}
                    color={getRoleColor(state.user?.role)}
                    size="small"
                  />
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<Edit />}
                  disabled
                  sx={{ ml: 2 }}
                >
                  Edit Profile
                </Button>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* User Details */}
              <Typography variant="h6" gutterBottom>
                Account Information
              </Typography>
              
              <List disablePadding>
                {userDetails.map((detail, index) => (
                  <ListItem key={index} disablePadding sx={{ py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {detail.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={detail.label}
                      secondary={detail.value}
                      secondaryTypographyProps={{
                        variant: 'body1',
                        color: 'text.primary',
                        fontWeight: 500
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Actions Card */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Account Actions
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<Edit />}
                  fullWidth
                  disabled
                >
                  Edit Profile
                </Button>
                
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<Logout />}
                  onClick={handleLogout}
                  fullWidth
                >
                  Logout
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* App Info Card */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                About {appConfig.name}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" paragraph>
                {appConfig.description}
              </Typography>
              
              <Typography variant="caption" color="text.secondary">
                Version {appConfig.version}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Profile; 